import React, {useEffect, useMemo, useState} from 'react'
import {useGetBillingActivityQuery} from '../../services/residentPortalApi'
import {useAccountContext} from '../layout/AccountLayout'
import {getNavigationPaths} from '../../services/navigation'
import AlertMessage from '../common/AlertMessage'
import Breadcrumbs from '../navigation/Breadcrumbs'
import BillingSummary from './BillingSummary'
import TransactionList from '../common/TransactionList'
import BillingActions from './BillingActions'
import BillingBalanceSummary from './BillingBalanceSummary'
import Loader from '../common/Loader'
import {BillingPagination} from './BillingPagination'
import Alert from '../common/Alert'
import {formatDate, getLegacyBillingInfo, sortBy} from '../../services/utils'
import {BillingActivity} from '../../types/app-types'

const BillingPage = () => {
  const pageSize = 20
  const {accountId, account, reservation} = useAccountContext()
  const isCheckedOut = reservation.status === 'CheckedOut'
  const paths = getNavigationPaths(accountId)
  const [page, setPage] = useState(1)
  const [billingActivity, updateBillingActivity] = useState({
    totalPages: 0,
    transactions: [],
    billingActivity: null
  })
  const billingActivityQuery = useGetBillingActivityQuery({
    propertyId: account.propertyCode,
    reservationId: account.reservationId
  })

  const balance = isCheckedOut
    ? !isNaN(reservation?.balance?.amount)
      ? Math.abs(reservation?.balance?.amount)
      : null
    : billingActivity?.billingActivity?.pendingBalance

  const legacyBilling = getLegacyBillingInfo(account.propertyCode)

  const currPageData = useMemo(() => {
    const sortTxs =
      billingActivity.transactions.length > 0
        ? sortBy(billingActivity.transactions, 'date')
        : []
    return sortTxs.slice(pageSize * (page - 1), pageSize * page)
  }, [page, billingActivity])

  useEffect(() => {
    const billingActivity: BillingActivity = billingActivityQuery.data || {}
    let transactions = billingActivity.transactions || []
    const totalPages =
      transactions?.length > 0 ? Math.ceil(transactions.length / pageSize) : 0
    setPage(page)
    updateBillingActivity({
      totalPages,
      transactions,
      billingActivity
    })
    console.log(`currPageData`, currPageData)
  }, [billingActivityQuery.data])

  return (
    <section>
      <Breadcrumbs
        items={[
          {label: 'Account', path: paths.Account, active: false},
          {label: 'Billing', active: true}
        ]}
      />
      <div className="card-default w-full">
        <div className="card-body-default">
          <h1 className="card-header">Billing</h1>
          {billingActivityQuery.isLoading ? <Loader /> : ''}

          {!billingActivityQuery.isLoading &&
          !billingActivityQuery.data?.transactions ? (
            <AlertMessage message="No billing statements found" type="info" />
          ) : (
            ''
          )}

          {!billingActivityQuery.isLoading ? (
            <>
              {legacyBilling ? (
                <Alert className="my-2 w-full" type="info">
                  <p>
                    Welcome to the Placemakr Portal! Here, you’ll be able to
                    view and manage all current and future billings. To view
                    billings prior to{' '}
                    {formatDate(legacyBilling?.transitionDate)}, please log into
                    your previous account
                    <a
                      href={legacyBilling?.portalUrl}
                      target="_blank"
                      className="link mx-1"
                    >
                      here.
                    </a>
                  </p>
                </Alert>
              ) : (
                ''
              )}

              {isCheckedOut ? (
                <div className="bg-slate-100 rounded-md sm:rounded-lg px-8 py-4 w-full">
                  <BillingBalanceSummary
                    balance={balance}
                    isEndingBalance={isCheckedOut}
                  />
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2 w-full">
                  <div className="bg-slate-100 rounded-md sm:rounded-lg px-8 py-4 w-full sm:w-1/2">
                    <BillingBalanceSummary
                      balance={balance}
                      isEndingBalance={isCheckedOut}
                    />
                  </div>
                  <div className="bg-slate-100 rounded-md sm:rounded-lg px-8 py-4 w-full sm:w-1/2">
                    <BillingSummary
                      billingActivity={billingActivity.billingActivity}
                    />
                  </div>
                </div>
              )}

              <div className="w-full">
                <BillingActions balance={balance} paths={paths} />
              </div>

              <div className="clear-both mt-3 mx-2">
                <h2 className="font-bold">Recent Transactions</h2>
                <TransactionList transactions={currPageData} />
                <BillingPagination
                  page={page}
                  totalPages={billingActivity.totalPages}
                  handlePagination={e => setPage(e)}
                />
              </div>
            </>
          ) : (
            ''
          )}
        </div>
      </div>
    </section>
  )
}

export default BillingPage
